<template>
    <SettingsPage title="Apothekengruppen">
        <Info>
            Hinweis: Die Reihenfolge der Apotheken hat einen Einfluss auf die Preiskalkulation. Der
            EK-Preis, aktueller VK-Preis und Lager werden von der ersten Apotheke bestimmt, die
            diesen Artikel verkauft hat.
        </Info>
        <div id="group-wrapper">
            <div
                v-for="pharmacyGroup in pharmacyGroups"
                :key="'pharmacyGroup_' + pharmacyGroup.id"
                class="pharmacy-group"
            >
                <b-input-group>
                    <b-input :key="'name_input_' + pharmacyGroup.id" v-model="pharmacyGroup.name" />
                    <b-input-group-append>
                        <SmartButton
                            type="submit"
                            variant="success"
                            @click="updatePharmacyGroup(pharmacyGroup)"
                        >
                            Namen ändern
                        </SmartButton>
                    </b-input-group-append>
                </b-input-group>

                <div>
                    <draggable
                        :key="forceUpdateHelper"
                        v-model="pharmaciesForGroup[pharmacyGroup.id]"
                        draggable=".draggable-tab"
                        handle=".handle"
                        :class="{
                            'list-group': true,
                            'is-empty':
                                pharmaciesForGroup[pharmacyGroup.id] &&
                                pharmaciesForGroup[pharmacyGroup.id].length === 0
                        }"
                        tag="ol"
                        v-bind="{
                            animation: 200,
                            group: 'description',
                            disabled: false,
                            ghostClass: 'ghost'
                        }"
                        @change="update(pharmacyGroup, $event)"
                        @end="drag = false"
                        @start="drag = true"
                    >
                        <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                            <li
                                v-for="pharmacy in pharmaciesForGroup[pharmacyGroup.id]"
                                :key="'pharmacy_' + pharmacy.id"
                                :class="{
                                    'draggable-tab': true,
                                    pharmacy: true
                                }"
                            >
                                <font-awesome-icon icon="grip-lines" class="handle" />
                                {{ pharmacy.name }}
                            </li>
                        </transition-group>
                    </draggable>
                </div>
                <AreYouSure
                    v-if="pharmacyGroup.id"
                    :key="'delete_' + pharmacyGroup.id"
                    confirm-msg="Apothekengruppe löschen!"
                    @confirm="deletePharmacyGroup(pharmacyGroup.id)"
                >
                    <b-button variant="danger">Apothekengruppe löschen?</b-button>
                </AreYouSure>
            </div>
            <div id="pharmacies-without-group">
                <b>Apotheken ohne zugeordnete Gruppe</b>
                <draggable
                    :key="forceUpdateHelper"
                    v-model="pharmaciesForGroup['withoutGroup']"
                    draggable=".draggable-tab"
                    handle=".handle"
                    :class="{
                        'list-group': true,
                        'is-empty':
                            pharmaciesForGroup['withoutGroup'] &&
                            pharmaciesForGroup['withoutGroup'].length === 0
                    }"
                    tag="ol"
                    v-bind="{
                        animation: 200,
                        group: 'description',
                        disabled: false,
                        ghostClass: 'ghost'
                    }"
                    @drop="update(false, $event)"
                    @end="drag = false"
                    @start="drag = true"
                >
                    <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                        <li
                            v-for="pharmacy in pharmaciesForGroup['withoutGroup']"
                            :key="'pharmacy_' + pharmacy.id"
                            :class="{
                                'draggable-tab': true,
                                pharmacy: true
                            }"
                        >
                            <font-awesome-icon icon="grip-lines" class="handle" />
                            {{ pharmacy.name }}
                        </li>
                    </transition-group>
                </draggable>
            </div>
        </div>
        <div id="add-pharmacyGroup">
            <b>Apothekengruppe hinzufügen</b>
            <b-input-group>
                <b-input v-model="newPharmacyGroupName" />
                <b-input-group-append>
                    <SmartButton
                        :disabled="!newPharmacyGroupName"
                        type="submit"
                        variant="success"
                        @click="addPharmacyGroup"
                    >
                        Anlegen
                    </SmartButton>
                </b-input-group-append>
            </b-input-group>
        </div>
    </SettingsPage>
</template>

<script>
import AreYouSure from "@/views/shared/widgets/AreYouSure";
import PharmacyGroup from "@/store/api/data/PharmacyGroup";
import draggable from "vuedraggable";
import Info from "../../components/widgets/Info";
import SettingsPage from "./SettingsPage";
import SmartButton from "@/views/shared/widgets/SmartButton";

export default {
    name: "Groups",
    components: { SmartButton, SettingsPage, Info, AreYouSure, draggable },
    data() {
        return {
            loading: true,
            drag: false,
            pharmaciesForGroup: {},
            newPharmacyGroupName: undefined
        };
    },
    computed: {
        pharmacyGroups() {
            return this.$store.state.data.pharmacyGroup;
        }
    },
    mounted() {
        this.load();
    },
    methods: {
        load() {
            this.$store.dispatch("loadPharmacies");
            this.$store.dispatch("loadPharmacyGroups").then(res => {
                const pharmacyGroups = res.data;
                pharmacyGroups.forEach(pharmacyGroup => {
                    this.pharmaciesForGroup[
                        pharmacyGroup.id
                    ] = this.$store.state.data.pharmacies.filter(
                        pharmacy => pharmacy.pharmacyGroupId === pharmacyGroup.id
                    );
                });
                this.pharmaciesForGroup["withoutGroup"] = this.$store.state.data.pharmacies.filter(
                    pharmacy => pharmacy.pharmacyGroupId === null
                );
                this.loading = false;
                this.forceUpdate();
            });
        },
        updatePharmacyGroup(pharmacyGroup) {
            PharmacyGroup.update(pharmacyGroup);
        },
        addPharmacyGroup() {
            PharmacyGroup.add({ name: this.newPharmacyGroupName }).then(() => {
                this.newPharmacyGroupName = undefined;
                this.load();
            });
        },
        deletePharmacyGroup(id) {
            // first, delete
            PharmacyGroup.unassignPharmacyFrom(
                id,
                this.pharmaciesForGroup[id].map(pharmacy => pharmacy.id)
            );
            PharmacyGroup.delete(id).then(() => {
                this.load();
            });
        },
        update(pharmacyGroup, event) {
            console.log(pharmacyGroup.id, { event });
            const pharmacy = event[Object.keys(event)[0]].element;
            if (event.removed) {
                console.log("unassignPharmacyFrom", pharmacyGroup, pharmacy);
                PharmacyGroup.unassignPharmacyFrom(pharmacyGroup.id, [pharmacy.id]);
            }
            if (event.added) {
                console.log("assignPharmacyTo", pharmacyGroup, pharmacy);
                PharmacyGroup.assignPharmacyTo(pharmacyGroup.id, [pharmacy.id]);
            }
            if (event.moved) {
                console.log("updateOrder");
                PharmacyGroup.updateOrder(
                    pharmacyGroup.id,
                    this.pharmaciesForGroup[pharmacyGroup.id].map(pharmacy => pharmacy.id)
                );
            }
        }
    }
};
</script>

<style scoped lang="scss">
#group-wrapper {
    display: flex;
    flex-wrap: wrap;
}
.pharmacy-group,
#pharmacies-without-group {
    min-width: 260px;
    margin: 5px;
    border: 3px solid $gray;
    padding: 7px;
    flex: 1;
}
#pharmacies-without-group {
    border-style: dotted;
}
.list-group {
    margin: 5px 0 5px 15px;
    box-sizing: border-box;
}
.list-group.is-empty {
    margin: 5px;
    min-height: 40px;
    background: $gray-lighter;
    border-radius: 4px;
}
.pharmacy {
    margin: 6px;
    padding: 4px 8px;
    background: rgb(232, 232, 232);
    border-radius: 4px;
    border: 1px solid $primary;
    transition: all 200ms cubic-bezier(0.43, 0, 0.37, 0.94);
}
.handle {
    margin-right: 8px;
    cursor: grab;
}

#pharmacies-without-group {
    .pharmacy {
        border: 1px solid $success;
        cursor: pointer;
        div {
            display: inline-flex;
            height: 100%;
            width: 1em;
            justify-content: center;
            align-items: center;
            background: $success;
        }
    }
}
#add-pharmacyGroup {
    padding-bottom: 25px;
}
</style>
